package com.castly.admin.ui.actions

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AuditActorDto
import com.castly.shared.dto.AuditLogEntryDto
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.jetbrains.compose.web.attributes.disabled

private const val PAGE_SIZE = 20

@Composable
fun ActionsHistoryPage(apiClient: ApiClient) {
    val colors = AppTheme.colors
    var entries by remember { mutableStateOf<List<AuditLogEntryDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var totalCount by remember { mutableStateOf(0L) }
    var currentPage by remember { mutableStateOf(1) } // 1-indexed for display/Pagination
    var filterState by remember { mutableStateOf(AuditLogFilterState()) }
    var selectedEntry by remember { mutableStateOf<AuditLogEntryDto?>(null) }
    var detailLoading by remember { mutableStateOf(false) }
    var exporting by remember { mutableStateOf(false) }
    var exportError by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()
    val isAdmin = remember { window.localStorage.getItem("user_role")?.uppercase() in setOf("ADMIN", "SUPER_ADMIN") }
    var actors by remember { mutableStateOf<List<AuditActorDto>>(emptyList()) }

    LaunchedEffect(Unit) {
        if (isAdmin) {
            try { actors = apiClient.listAuditLogActors() } catch (_: Exception) {}
        }
    }

    fun exportCsv() {
        scope.launch {
            exporting = true
            exportError = null
            try {
                val dateFrom = filterState.dateFrom.takeIf { it.isNotBlank() }?.let { "${it}T00:00:00Z" }
                val dateTo = filterState.dateTo.takeIf { it.isNotBlank() }?.let { "${it}T23:59:59Z" }
                val csvText = apiClient.exportAuditLog(
                    actionType = filterState.actionType.takeIf { it.isNotBlank() },
                    entityType = filterState.entityType.takeIf { it.isNotBlank() },
                    keyword = filterState.keyword.takeIf { it.isNotBlank() },
                    dateFrom = dateFrom,
                    dateTo = dateTo
                )
                val blob = Blob(arrayOf(csvText.asDynamic()), BlobPropertyBag(type = "text/csv"))
                val blobUrl = URL.createObjectURL(blob)
                val link = document.createElement("a").unsafeCast<HTMLAnchorElement>()
                link.href = blobUrl
                link.download = "audit-log.csv"
                document.body?.appendChild(link)
                link.click()
                document.body?.removeChild(link)
                URL.revokeObjectURL(blobUrl)
            } catch (e: Exception) {
                exportError = e.message ?: "Export failed"
            } finally {
                exporting = false
            }
        }
    }

    fun openDetail(entry: AuditLogEntryDto) {
        // Show the panel immediately with the list data, then refresh from the detail endpoint
        selectedEntry = entry
        scope.launch {
            detailLoading = true
            try {
                selectedEntry = apiClient.getAuditLogEntry(entry.id)
            } catch (_: Exception) {
                // Keep the list entry if the detail fetch fails
            } finally {
                detailLoading = false
            }
        }
    }

    fun loadEntries() {
        scope.launch {
            loading = true
            error = null
            try {
                // Convert date-only strings (YYYY-MM-DD) to ISO-8601 for the backend
                val dateFrom = filterState.dateFrom.takeIf { it.isNotBlank() }
                    ?.let { "${it}T00:00:00Z" }
                val dateTo = filterState.dateTo.takeIf { it.isNotBlank() }
                    ?.let { "${it}T23:59:59Z" }

                val response = apiClient.listAuditLog(
                    page = currentPage - 1, // convert to 0-indexed for backend
                    pageSize = PAGE_SIZE,
                    actionType = filterState.actionType.takeIf { it.isNotBlank() },
                    entityType = filterState.entityType.takeIf { it.isNotBlank() },
                    keyword = filterState.keyword.takeIf { it.isNotBlank() },
                    dateFrom = dateFrom,
                    dateTo = dateTo,
                    actorId = filterState.actorId.takeIf { it.isNotBlank() },
                    labelName = filterState.labelName.takeIf { it.isNotBlank() }
                )
                entries = response.entries
                totalCount = response.totalCount
            } catch (e: Exception) {
                error = e.message ?: "Failed to load actions history"
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(currentPage, filterState) {
        loadEntries()
    }

    Div({
        style {
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color(colors.surfaceHover))
            themeTransition()
        }
    }) {
        // Header
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surface))
                property("border-bottom", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                property("flex-shrink", "0")
                themeTransition()
            }
        }) {
            H2({
                style {
                    margin(0.px)
                    fontSize(Typography.lg)
                    fontWeight("600")
                    color(Color(colors.text))
                }
            }) {
                Text("Actions History")
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.md)
                }
            }) {
                if (!loading && totalCount > 0) {
                    Span({
                        style {
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                        }
                    }) {
                        Text("$totalCount total entries")
                    }
                }
                Button({
                    onClick { if (!exporting) exportCsv() }
                    if (exporting) disabled()
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        fontSize(Typography.sm)
                        color(Color(if (exporting) colors.textMuted else colors.text))
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        cursor(if (exporting) "not-allowed" else "pointer")
                        themeTransition()
                    }
                }) {
                    Text(if (exporting) "Exporting..." else "Export CSV")
                }
            }
        }

        // Content
        Div({
            style {
                flex(1)
                minHeight(0.px)
                overflow("auto")
                padding(Spacing.lg)
            }
        }) {
            // Filter bar
            AuditLogFilters(
                filterState = filterState,
                onFilterChange = { newState ->
                    filterState = newState
                    currentPage = 1 // reset to first page on filter change
                },
                isAdmin = isAdmin,
                actors = actors
            )

            // Export error
            exportError?.let { errMsg ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        border(1.px, LineStyle.Solid, Color(colors.danger))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.md)
                        marginBottom(Spacing.lg)
                        themeTransition()
                    }
                }) {
                    Span({
                        style {
                            fontSize(Typography.sm)
                            color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                        }
                    }) { Text("Export failed: $errMsg") }
                }
            }

            // Loading state
            if (loading) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        padding(48.px)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text("Loading actions history...")
                }
            }

            // Error state
            error?.let { errorMsg ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        border(1.px, LineStyle.Solid, Color(colors.danger))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.lg)
                        marginBottom(Spacing.lg)
                        themeTransition()
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.lg)
                            fontWeight("600")
                            color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                            marginBottom(Spacing.sm)
                        }
                    }) { Text("Error") }
                    Text(errorMsg)
                }
            }

            // Empty state
            if (!loading && error == null && entries.isEmpty()) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        padding(48.px)
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.lg)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        themeTransition()
                    }
                }) {
                    H3({
                        style {
                            fontSize(Typography.xl)
                            fontWeight("600")
                            color(Color(colors.text))
                            margin(0.px)
                            marginBottom(Spacing.sm)
                        }
                    }) {
                        Text(if (filterState.isActive) "No matching entries" else "No actions recorded yet")
                    }
                    P({ style { color(Color(colors.textMuted)); margin(0.px) } }) {
                        Text(
                            if (filterState.isActive) "Try adjusting or clearing the filters"
                            else "Admin actions will appear here once they occur"
                        )
                    }
                }
            }

            // Table
            if (!loading && error == null && entries.isNotEmpty()) {
                Div({
                    style {
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.lg)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        overflow("hidden")
                        themeTransition()
                    }
                }) {
                    // Table header
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "180px 120px 110px 110px 120px 160px 1fr")
                            padding(Spacing.md, Spacing.xl)
                            backgroundColor(Color(colors.surfaceHover))
                            property("border-bottom", "1px solid ${colors.border}")
                        }
                    }) {
                        listOf("Timestamp", "Label", "Actor", "Action", "Entity Type", "Item ID", "Entity Name").forEach { col ->
                            Span({
                                style {
                                    fontSize(Typography.sm)
                                    fontWeight("600")
                                    color(Color(colors.textMuted))
                                    property("text-transform", "uppercase")
                                    property("letter-spacing", "0.05em")
                                }
                            }) { Text(col) }
                        }
                    }

                    // Table rows
                    entries.forEachIndexed { index, entry ->
                        key(entry.id) {
                            AuditLogRow(entry = entry, onOpen = { openDetail(entry) })
                            if (index < entries.size - 1) {
                                Div({
                                    style {
                                        height(1.px)
                                        backgroundColor(Color(colors.border))
                                    }
                                })
                            }
                        }
                    }
                }

                // Pagination
                if (totalCount > PAGE_SIZE) {
                    val totalPages = ((totalCount + PAGE_SIZE - 1) / PAGE_SIZE).toInt()
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                            marginTop(Spacing.lg)
                            fontSize(Typography.base)
                        }
                    }) {
                        val startItem = (currentPage - 1) * PAGE_SIZE + 1
                        val endItem = minOf(currentPage * PAGE_SIZE, totalCount.toInt())
                        Span({ style { color(Color(colors.textMuted)) } }) {
                            Text("Showing $startItem–$endItem of $totalCount")
                        }
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                gap(8.px)
                            }
                        }) {
                            Button({
                                onClick { currentPage = maxOf(1, currentPage - 1) }
                                if (currentPage <= 1) disabled()
                                style {
                                    padding(Spacing.sm, Spacing.lg)
                                    fontSize(Typography.base)
                                    color(if (currentPage <= 1) Color(colors.textMuted) else Color(colors.text))
                                    backgroundColor(Color(colors.surface))
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    cursor(if (currentPage <= 1) "not-allowed" else "pointer")
                                }
                            }) { Text("Previous") }

                            Button({
                                onClick { currentPage = minOf(totalPages, currentPage + 1) }
                                if (currentPage >= totalPages) disabled()
                                style {
                                    padding(Spacing.sm, Spacing.lg)
                                    fontSize(Typography.base)
                                    color(if (currentPage >= totalPages) Color(colors.textMuted) else Color(colors.text))
                                    backgroundColor(Color(colors.surface))
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    cursor(if (currentPage >= totalPages) "not-allowed" else "pointer")
                                }
                            }) { Text("Next") }
                        }
                    }
                }
            }
        }
    }

    // Detail panel — rendered outside the scrolling content div (fixed overlay)
    if (selectedEntry != null || detailLoading) {
        AuditLogDetailPanel(
            entry = selectedEntry,
            loading = detailLoading && selectedEntry == null,
            onClose = {
                selectedEntry = null
                detailLoading = false
            }
        )
    }
}

@Composable
private fun AuditLogRow(entry: AuditLogEntryDto, onOpen: () -> Unit) {
    val colors = AppTheme.colors

    Div({
        onClick { onOpen() }
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "180px 120px 110px 110px 120px 160px 1fr")
            padding(Spacing.md, Spacing.xl)
            property("transition", "background-color 0.15s")
            cursor("pointer")
        }
        onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
        onMouseOut { it.target.asDynamic().style.backgroundColor = "transparent" }
    }) {
        // Timestamp
        Span({
            style {
                fontSize(Typography.sm)
                color(Color(colors.textMuted))
                property("font-variant-numeric", "tabular-nums")
            }
        }) {
            Text(formatTimestamp(entry.createdAt))
        }

        // Label
        Span({
            style {
                fontSize(Typography.sm)
                color(Color(if (entry.labelName != null) colors.text else colors.textMuted))
                property("text-overflow", "ellipsis")
                overflow("hidden")
                property("white-space", "nowrap")
            }
        }) {
            Text(entry.labelName ?: "—")
        }

        // Actor
        Span({
            style {
                fontSize(Typography.base)
                color(if (entry.actorDisplayName == "[Hidden]") Color(colors.textMuted) else Color(colors.text))
                property("font-style", if (entry.actorDisplayName == "[Hidden]") "italic" else "normal")
                property("text-overflow", "ellipsis")
                overflow("hidden")
                property("white-space", "nowrap")
            }
        }) {
            Text(entry.actorDisplayName)
        }

        // Action type badge
        ActionBadge(entry.actionType)

        // Entity type
        Span({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
            }
        }) {
            Text(entry.entityType)
        }

        // Item ID
        Span({
            style {
                fontSize(Typography.xs)
                color(Color(colors.textMuted))
                property("font-family", "monospace")
                property("text-overflow", "ellipsis")
                overflow("hidden")
                property("white-space", "nowrap")
            }
        }) {
            Text(entry.entityId ?: "—")
        }

        // Entity name
        Span({
            style {
                fontSize(Typography.base)
                color(Color(colors.text))
                property("text-overflow", "ellipsis")
                overflow("hidden")
                property("white-space", "nowrap")
            }
        }) {
            Text(entry.entityName ?: "—")
        }
    }
}

@Composable
private fun ActionBadge(actionType: String) {
    val colors = AppTheme.colors
    val (bg, fg) = when (actionType.lowercase()) {
        "create" -> Pair("#dcfce7", "#166534")
        "delete" -> Pair("#fee2e2", "#991b1b")
        "update" -> Pair("#dbeafe", "#1e40af")
        "login" -> Pair("#f3e8ff", "#6b21a8")
        "logout" -> Pair("#fef3c7", "#92400e")
        "publish" -> Pair("#d1fae5", "#065f46")
        "unpublish" -> Pair("#fef9c3", "#854d0e")
        "import", "bulkupdate" -> Pair("#e0e7ff", "#3730a3")
        else -> Pair(colors.surfaceHover, colors.textMuted)
    }
    Span({
        style {
            display(DisplayStyle.InlineBlock)
            padding(2.px, 8.px)
            borderRadius(BorderRadius.sm)
            fontSize(12.px)
            fontWeight("600")
            backgroundColor(Color(bg))
            color(Color(fg))
            property("white-space", "nowrap")
        }
    }) {
        Text(actionType)
    }
}

private fun formatTimestamp(iso: String): String {
    // Parse "2026-02-22T14:35:00.000Z" → "Feb 22, 14:35"
    return try {
        val t = iso.replace("T", " ").substringBefore(".")
        // "2026-02-22 14:35:00" → "2026-02-22 14:35"
        t.substringBeforeLast(":").takeLast(16)
    } catch (_: Throwable) {
        iso.take(16)
    }
}
